import { analiticaInstance } from 'shared/analitica'
import { appEnv } from 'shared/envs'

/**
 * Log levels for surveys logging
 */
export enum SurveysLogLevel {
  ERROR = 'error',
  WARN = 'warn',
  INFO = 'info'
}

/**
 * Interface for log data structure
 */
export interface SurveysLogData {
  // others
  description: string
  level: SurveysLogLevel
  timestamp: string
  context?: Record<string, unknown>
  error?: Error
}

const defaultMetadata = {
  owner: 'credit',
  product: 'internalização de pesquisa',
  screen: 'surveys',
  project: 'stone', // Default for stone. Should be overridden ny app platform of webview.
  filterPlugins: ['DATADOG']
}

/**
 * Analytics events for Datadog integration in production
 */
export const surveysLogAnalytics = analiticaInstance.eventFactory('SurveysInAppLogs', {
  logging: {
    /**
     * @analitica log event
     */
    onLog: (data: SurveysLogData) => {
      return {
        label: String(data.context?.label) || 'surveys-log-event',
        metadata: {
          ...defaultMetadata,
          level: data.level,
          description: data.description,
          timestamp: data.timestamp,
          context: data.context,
          error: data.error
            ? {
                name: data.error.name,
                message: data.error.message,
                stack: data.error.stack
              }
            : undefined
        }
      }
    },

    /**
     * @analitica error event
     */
    onError: (data: SurveysLogData) => {
      return {
        label: String(data.context?.label) || 'surveys-error-event',
        metadata: {
          ...defaultMetadata,
          description: data.description,
          timestamp: data.timestamp,
          context: data.context,
          error: data.error
            ? {
                name: data.error.name,
                message: data.error.message,
                stack: data.error.stack
              }
            : undefined
        }
      }
    },

    /**
     * @analitica warn event
     */
    onWarn: (data: SurveysLogData) => {
      return {
        label: String(data.context?.label) || 'surveys-warn-event',
        metadata: {
          ...defaultMetadata,
          description: data.description,
          timestamp: data.timestamp,
          context: data.context
        }
      }
    }
  }
})

/**
 * Utility class for surveys logging
 *
 * Uses new taxonomy
 * Prefixes all messages with "[Surveys]"
 * Uses console.log in non-production environments
 * Sends events to Datadog via analitica in production
 * Supports different log levels (error, warn, info, debug)
 * Includes context data and error objects
 */
export class SurveysLogger {
  private static readonly PREFIX = '[Surveys]'
  public static readonly isProduction = appEnv.isProduction()

  /**
   * Formats description with prefix for console output
   */
  private static formatMessage(description: string): string {
    return `${this.PREFIX} ${description}`
  }

  /**
   * Get webview context where the page was open
   */
  public static getWebviewContext() {
    return {
      userAgent: navigator.userAgent,
      url: typeof window !== 'undefined' ? window.location.href : '',
      referrer: typeof window.document !== 'undefined' ? window.document.referrer : ''
    }
  }

  /**
   * Handles console logging for non-production environments
   */
  private static logToConsole(data: SurveysLogData): void {
    const formattedMessage = this.formatMessage(data.description)
    const logData = data.context ? [formattedMessage, data.context] : [formattedMessage]

    switch (data.level) {
      case SurveysLogLevel.ERROR:
        if (data.error) {
          // eslint-disable-next-line no-console
          console.error(formattedMessage, data.error, data.context)
        } else {
          // eslint-disable-next-line no-console
          console.error(...logData)
        }
        break
      case SurveysLogLevel.WARN:
        // eslint-disable-next-line no-console
        console.warn(...logData)
        break
      case SurveysLogLevel.INFO:
        // eslint-disable-next-line no-console
        console.info(...logData)
        break
      default:
        // eslint-disable-next-line no-console
        console.log(...logData)
    }
  }

  /**
   * Handles analytics logging
   */
  private static logToAnalytics(data: SurveysLogData): void {
    try {
      switch (data.level) {
        case SurveysLogLevel.ERROR:
          surveysLogAnalytics.events.logging.onError(data)
          break
        case SurveysLogLevel.WARN:
          surveysLogAnalytics.events.logging.onWarn(data)
          break
        default:
          surveysLogAnalytics.events.logging.onLog(data)
      }
    } catch (error) {
      // Fallback to console if analytics fails
      // eslint-disable-next-line no-console
      console.error(this.formatMessage('Failed to send log to analytics:'), error)
    }
  }

  /**
   * Core logging method
   */
  private static logMessage(
    description: string,
    level: SurveysLogLevel,
    context?: Record<string, unknown>,
    error?: Error
  ): void {
    const logData = {
      description,
      level,
      timestamp: new Date().toISOString(),
      context,
      error
    }

    if (this.isConsoleLogging()) {
      this.logToConsole(logData)
    }

    this.logToAnalytics(logData)
  }

  /**
   * Log an informational description
   *
   * @param description - The description to log
   * @param context - Optional context data
   *
   * @example
   * SurveysLogger.info('Survey loaded successfully', { surveyId: 'survey-123' })
   */
  static info(description: string, context?: Record<string, unknown>): void {
    this.logMessage(description, SurveysLogLevel.INFO, context)
  }

  /**
   * Log a warning description
   *
   * @param description - The description to log
   * @param context - Optional context data
   *
   * @example
   * SurveysLogger.warn('Survey response validation failed', { errors: ['Invalid score'] })
   */
  static warn(description: string, context?: Record<string, unknown>): void {
    this.logMessage(description, SurveysLogLevel.WARN, context)
  }

  /**
   * Log an error description
   *
   * @param description - The description to log
   * @param error - Optional error object
   * @param context - Optional context data
   *
   * @example
   * SurveysLogger.error('Failed to submit survey', error, { surveyId: 'survey-123' })
   */
  static error(description: string, error?: Error, context?: Record<string, unknown>): void {
    this.logMessage(description, SurveysLogLevel.ERROR, context, error)
  }

  /**
   * Default log method (same as info)
   *
   * @param description - The description to log
   * @param context - Optional context data
   *
   * @example
   * SurveysLogger.log('Default log description')
   */
  static log(description: string, context?: Record<string, unknown>): void {
    this.info(description, context)
  }

  /**
   * Check if logging to console (non-production)
   */
  static isConsoleLogging(): boolean {
    return !this.isProduction
  }

  /**
   * Check if logging to analytics (production)
   */
  static isAnalyticsLogging(): boolean {
    return this.isProduction
  }
}

export default SurveysLogger
